import { LoadingOutlined } from '@ant-design/icons';
import { useMount } from 'ahooks';
import { message, ModalProps } from 'antd';
import Dialogue from 'components/Dialogue';
import { JustRightHeightModal } from 'components/styled/scoped';
import { bundleMsgTranslateReport, getBundleMsg } from 'services/api/conversation';
import useMessageRender from '../ChatMain/useMessageRender';
import { useGetNoTransedMsgs, useUiMessage } from '../../useChatView';
import { DialogueModeContext } from '../DialogueModeContext.tsx';
import useCrud from 'hooks/useCrud.ts';
import sysLangToTranslationLang from 'utils/sysLangToTranslationLang.ts';
import { LocalMessage } from 'hooks/business/conversation.ts';
import { overseaTranslate } from 'services/api/overseaTranslate.ts';
import { useIsSbsMode } from 'hooks/business/useIsSbs.ts';

function FaqModal({
  conversation,
  msgId,
  ...otherProps
}: ModalProps & {
  conversation: Conversation.Conversation;
  msgId: string;
}) {
  const { t, messageRender } = useMessageRender(conversation);

  const { sysLang, getNoTransedMsgs } = useGetNoTransedMsgs(conversation);

  const isSbs = useIsSbsMode();

  const {
    loading,
    list: messages,
    setList,
    fetchList,
  } = useCrud({
    readList: () =>
      getBundleMsg({
        conversationId: conversation.conversationId,
        msgId,
        isSbs,
      }).then((res) => res.message.reverse() as LocalMessage[]),
  });

  useMount(() => {
    fetchList()
      .then((messages) => {
        const noTransed = getNoTransedMsgs({
          messages: messages || [],
        });

        if (noTransed.length) {
          const languageKey = sysLangToTranslationLang(sysLang);
          setList((pre) => {
            if (!pre) {
              return pre;
            }

            return noTransed.reduce(
              (res, cur) => {
                const targetIndex = res.findIndex((_) => _.msgId === cur.msgId);
                if (targetIndex < 0) {
                  return res;
                }
                res[targetIndex] = {
                  ...res[targetIndex],
                  hideTranslate: false,
                  textContent: {
                    ...(res[targetIndex].textContent as any),
                    translationContent: {
                      ...res[targetIndex].textContent?.translationContent,
                      [languageKey]: {
                        language: languageKey,
                        status: 'pending',
                        text: '',
                        translated: false,
                      },
                    },
                  },
                };
                return res;
              },
              [...pre],
            );
          });
          const texts = noTransed.map((_) =>
            _.messageType === 'TEXT'
              ? _.textContent?.text || ''
              : _.messageType === 'IMAGE_WITH_TEXT'
              ? _.imageWithTextContent?.text || ''
              : '',
          );
          overseaTranslate(texts, sysLang, 'chat', noTransed[0].region)
            .then((transed) => {
              const reportBody: {
                msgId: string;
                translation: {
                  text: string;
                  source: string;
                  targetLanguage: string;
                };
              }[] = [];
              setList((pre) => {
                if (!pre) {
                  return pre;
                }

                return transed.reduce(
                  (res, cur, index) => {
                    const targetIndex = res.findIndex((_) => _.msgId === noTransed[index].msgId);
                    if (targetIndex < 0) {
                      return res;
                    }
                    res[targetIndex] = {
                      ...res[targetIndex],
                      textContent: {
                        ...(res[targetIndex].textContent as any),
                        translationContent: {
                          ...res[targetIndex].textContent?.translationContent,
                          [languageKey]: {
                            text: cur,
                            source: 'chatpp',
                            targetLanguage: languageKey,
                            status: 'success',
                            translated: true,
                          },
                        },
                      },
                    };
                    reportBody.push({
                      msgId: res[targetIndex].msgId,
                      translation: {
                        text: cur,
                        source: 'chatpp',
                        targetLanguage: languageKey,
                      },
                    });
                    return res;
                  },
                  [...pre],
                );
              });
              if (reportBody.length) {
                bundleMsgTranslateReport({ translateReportInfo: reportBody }).catch(() => {});
              }
            })
            .catch(() => {
              message.error(t('common.feedback.translationFailed'));
              setList((pre) => {
                if (!pre) {
                  return pre;
                }

                return noTransed.reduce(
                  (res, cur) => {
                    const targetIndex = res.findIndex((_) => _.msgId === cur.msgId);
                    if (targetIndex < 0) {
                      return res;
                    }
                    res[targetIndex] = {
                      ...res[targetIndex],
                      hideTranslate: false,
                      textContent: {
                        ...(res[targetIndex].textContent as any),
                        translationContent: {
                          ...res[targetIndex].textContent?.translationContent,
                          [languageKey]: undefined,
                        },
                      },
                    };
                    return res;
                  },
                  [...pre],
                );
              });
            });
        }
      })
      .catch(() => {
        message.error(t('common.feedback.fetchFailed'));
      });
  });

  const { uiMessages } = useUiMessage({
    messages: messages || [],
    conversation,
  });

  return (
    <JustRightHeightModal
      width={600}
      title={t('scene.chatWorkspace.messageScenario.action.viewAllFaq')}
      footer={false}
      {...otherProps}
    >
      {loading ? (
        <LoadingOutlined />
      ) : messages ? (
        <DialogueModeContext.Provider value="faq">
          <Dialogue messages={uiMessages} messageRender={messageRender} />
        </DialogueModeContext.Provider>
      ) : null}
    </JustRightHeightModal>
  );
}

export default FaqModal;
